import { renderCriteriaChipsUi } from './criteria-chips-renderer.js'
import {
  bindHandoffActions,
  normalizeHandoffActions,
  renderHandoffActionsEmbed
} from './capability-block-view-renderer.js'
import { renderLucideIcon } from '../utils/lucide-icons.js'

function normalizeTableUi(result) {
  if (!result || typeof result !== 'object') return null
  var ui = result.ui || result.view || result.display || null
  if (!ui || typeof ui !== 'object') return null
  if (String(ui.type || '').toLowerCase() !== 'table') return null

  var columns = Array.isArray(ui.columns) ? ui.columns : []
  var rows = Array.isArray(ui.rows) ? ui.rows : []
  var pagination = normalizePaginationUi(ui.pagination || ui.paging || null)

  return {
    title: ui.title || ui.label || '',
    note: ui.note || ui.description || '',
    criteriaUi: ui.criteriaUi || null,
    handoffActions: normalizeHandoffActions(ui.handoffActions || result.handoffActions),
    columns: columns.map(function (column) {
      if (typeof column === 'string') {
        return { key: column, label: column }
      }
      return {
        key: column.key || column.field || column.name || column.label || '',
        label: column.label || column.name || column.key || column.field || ''
      }
    }),
    rows: rows,
    pagination: pagination,
    radarRunId: normalizeRadarRunId(result)
  }
}

function bindTableUi(root, tableUi, handlers) {
  bindHandoffActions(root, tableUi && tableUi.handoffActions, handlers)
}

function normalizeRadarRunId(result) {
  if (!result || typeof result !== 'object') return ''
  var queryState = result.queryState && typeof result.queryState === 'object' ? result.queryState : null
  var radarRunId = queryState ? queryState.radarRunId : result.radarRunId
  return typeof radarRunId === 'string' ? radarRunId : ''
}

function normalizePaginationUi(value) {
  if (!value || typeof value !== 'object') return null

  var page = toPositiveInteger(value.page, 1)
  var pageSize = toPositiveInteger(value.pageSize || value.length, 20)
  var total = toNonNegativeInteger(value.total, 0)
  var totalPages = toPositiveInteger(value.totalPages, Math.max(1, Math.ceil(total / pageSize)))
  var start = toNonNegativeInteger(value.start, (page - 1) * pageSize)
  var length = toNonNegativeInteger(value.length, 0)

  return {
    page: page,
    pageSize: pageSize,
    start: start,
    length: length,
    total: total,
    totalPages: totalPages,
    hasPrevious: Boolean(value.hasPrevious),
    hasNext: Boolean(value.hasNext),
    targetStepKey: value.targetStepKey || value.stepKey || 'QueryResult'
  }
}

function toPositiveInteger(value, fallback) {
  var normalized = Number(value)
  return Number.isFinite(normalized) && normalized > 0 ? Math.floor(normalized) : fallback
}

function toNonNegativeInteger(value, fallback) {
  var normalized = Number(value)
  return Number.isFinite(normalized) && normalized >= 0 ? Math.floor(normalized) : fallback
}

function renderTableUi(tableUi) {
  var columns = tableUi.columns || []
  var rows = tableUi.rows || []
  var pagination = tableUi.pagination || null
  var head = columns.length
    ? '<thead><tr>' + columns.map(function (column) {
        return '<th>' + escapeHtml(column.label || column.key || '') + '</th>'
      }).join('') + '</tr></thead>'
    : ''

  var body = rows.length
    ? '<tbody>' + rows.map(function (row) {
        var cells = columns.length
          ? columns.map(function (column) {
              var value = row && typeof row === 'object' ? row[column.key] : row
              return '<td>' + escapeHtml(value === undefined || value === null ? '' : String(value)) + '</td>'
            }).join('')
          : '<td>' + escapeHtml(JSON.stringify(row)) + '</td>'
        return '<tr>' + cells + '</tr>'
      }).join('') + '</tbody>'
    : '<tbody><tr><td class="wc-table-empty" colspan="' + Math.max(columns.length, 1) + '">Aucune donnée.</td></tr></tbody>'

  return (
    '<div class="wc-table-ui">' +
      '<div class="wc-ds-capability wc-ds-capability-radar">' +
        '<div class="wc-ds-capability-name">' + renderLucideIcon('search') + '<span>@Radar</span></div>' +
        '<div class="wc-ds-capability-meta">' + escapeHtml(tableUi.title || 'Résultats') + '</div>' +
      '</div>' +
      (tableUi.title ? '<div class="wc-table-title">' + escapeHtml(tableUi.title) + '</div>' : '') +
      (tableUi.criteriaUi ? renderCriteriaChipsUi(tableUi.criteriaUi) : '') +
      (tableUi.note ? '<div class="wc-table-note">' + escapeHtml(tableUi.note) + '</div>' : '') +
      '<div class="wc-table-scroll"><table class="wc-table-renderer">' + head + body + '</table></div>' +
      (pagination ? renderPaginationUi(pagination) : '') +
      renderHandoffActionsEmbed(tableUi.handoffActions) +
      (tableUi.radarRunId ? renderRadarFeedbackUi(tableUi.radarRunId) : '') +
    '</div>'
  )
}

function renderRadarFeedbackUi(radarRunId) {
  return (
    '<div class="wc-table-feedback" data-radar-run-id="' + escapeAttribute(radarRunId) + '">' +
      '<button type="button" class="wc-table-feedback-button" data-radar-feedback-rating="ok" aria-label="Résultat pertinent" aria-pressed="false" title="Résultat pertinent">' + renderLucideIcon('thumbsUp') + '</button>' +
      '<button type="button" class="wc-table-feedback-button" data-radar-feedback-rating="not_ok" aria-label="Résultat non pertinent" aria-pressed="false" title="Résultat non pertinent">' + renderLucideIcon('thumbsDown') + '</button>' +
    '</div>'
  )
}

function renderPaginationUi(pagination) {
  var from = pagination.length > 0 ? pagination.start + 1 : 0
  var to = pagination.start + pagination.length
  var label = pagination.total > 0
    ? from + '-' + to + ' sur ' + pagination.total
    : 'Page ' + pagination.page

  return (
    '<div class="wc-table-pagination">' +
      '<button type="button" class="wc-table-page" data-step-key="' + escapeAttribute(pagination.targetStepKey) + '" data-page="' + (pagination.page - 1) + '" data-page-size="' + pagination.pageSize + '"' + (pagination.hasPrevious ? '' : ' disabled') + '>' + renderLucideIcon('chevronLeft') + '<span>Précédent</span></button>' +
      '<span class="wc-table-page-label">' + escapeHtml(label) + '</span>' +
      '<button type="button" class="wc-table-page" data-step-key="' + escapeAttribute(pagination.targetStepKey) + '" data-page="' + (pagination.page + 1) + '" data-page-size="' + pagination.pageSize + '"' + (pagination.hasNext ? '' : ' disabled') + '><span>Suivant</span>' + renderLucideIcon('chevronRight') + '</button>' +
    '</div>'
  )
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function escapeAttribute(value) {
  return escapeHtml(value)
}

export { bindTableUi, normalizeTableUi, renderTableUi }
